<template>
    <div
        id="offices-map"
        class="offices-map relative"
    >
        <UIMap
            :key="type"
            :options="mapOptions"
            :markers="markers"
            with-geolocation
            @map-was-initialized="onMapInit"
            @click-on-cluster="onClusterClickHandler"
        />
        <transition name="fade">
            <OfficesMapBalloon
                v-if="currentBalloonData.length"
                :balloon-data="currentBalloonData[currentBalloonIndex]"
                :type="type"
                :balloons-total="currentBalloonData.length"
                :balloon-index="currentBalloonIndex"
                @close="currentBalloonData = []"
                @switch="onSwitchBalloonHandler"
            />
        </transition>
    </div>
</template>

<script>
import {computed, defineComponent, ref, watch} from 'vue';
import {useContext} from '@nuxtjs/composition-api';

import OfficesMapBalloon from '@/components/Pages/About/Offices/OfficesMapBalloon.vue';
import UIMap from '@/components/UI-components/Map/UIMap.vue';

const MIN_CLUSTER_BALLOON_ZOOM = 21;
const CLUSTER_ZOOM_STEPS = {
    DESKTOP: 4,
    MOBILE: 2,
};

export default defineComponent({
    name: 'OfficesMap',
    components: {
        OfficesMapBalloon,
        UIMap,
    },
    props: {
        controls: {
            type: Array,
            default: () => ['zoomControl'],
        },
        coords: {
            type: Array,
            default: () => [47.229089, 39.719043],
        },
        zoom: {
            type: [Number, String],
            default: () => 7,
        },
        balloonsData: {
            type: Array,
            required: true,
        },
        type: {
            type: String,
            default: () => '',
        },
    },
    emits: ['update:zoom', 'balloon-open'],
    setup(props, context) {
        const {$isMobileOrTablet, $img} = useContext();

        const map = ref({});
        const currentCoords = ref([]);
        const currentBalloonData = ref([]);
        const currentBalloonIndex = ref(0);

        const mapOptions = computed(() => {
            return {
                center: currentCoords.value,
                controls: props.controls,
                zoom: props.zoom,
                clusterOptions: {
                    icons: [{
                        href: '',
                        size: [49, 49],
                        offset: [-25, -25],
                    }],
                    iconCustomTemplate: `
                        <div class="office-cluster flex items-center justify-center">
                            <span class="office-cluster__counter">
                                {{ properties.geoObjects.length }}
                            </span>
                        </div>
                    `,
                    iconContentOffset: [-25, -25],
                    disableClickZoom: true,
                },
            };
        });

        const markers = computed(() => {
            return props.balloonsData.map((balloonData) => {
                return {
                    coordinates: [balloonData.latitude, balloonData.longitude],
                    iconLayout: 'default#image',
                    iconImageHref: $img('icons/map_logo.svg', {format: 'webp'}),
                    iconImageSize: [49, 49],
                    iconImageOffset: [-25, -25],
                    events: [
                        {
                            type: 'click',
                            payload: balloonData,
                            callback: onBalloonOpenHandler,
                        },
                    ],
                };
            });
        });

        watch(() => props.coords, (newCoords) => {
            currentCoords.value = newCoords;
        }, {immediate: true, deep: true});

        watch(() => props.type, () => {
            currentBalloonData.value = [];
        });

        function onClusterClickHandler(event) {
            event.stopPropagation();
            const zoom = map.value.getZoom();
            const cluster = event.get('target');
            currentBalloonIndex.value = 0;

            if (typeof cluster?.getGeoObjects !== 'function') {
                return;
            }

            if (zoom < MIN_CLUSTER_BALLOON_ZOOM) {
                const clusterCoords = $isMobileOrTablet
                    ? event.get('coords')
                    : cluster.geometry._coordinates;

                currentCoords.value = clusterCoords.slice(0);
                const currentZoomStep = $isMobileOrTablet
                    ? CLUSTER_ZOOM_STEPS.MOBILE
                    : CLUSTER_ZOOM_STEPS.DESKTOP;

                const increasedZoom = zoom + currentZoomStep;
                const newZoom = increasedZoom > MIN_CLUSTER_BALLOON_ZOOM
                    ? MIN_CLUSTER_BALLOON_ZOOM
                    : increasedZoom;

                context.emit('update:zoom', newZoom);

                return;
            }

            const markers = cluster.getGeoObjects();
            const clusterCoordinates = markers.reduce((acc, marker) => {
                const coordinates = marker.geometry.getCoordinates();

                if (acc.every((coord) => JSON.stringify(coord) !== JSON.stringify(coordinates))) {
                    acc.push(coordinates);
                }

                return acc;
            }, []);

            const balloons = props.balloonsData.filter((balloon) => {
                return clusterCoordinates.some((bound) => {
                    return parseFloat(bound[0]) === parseFloat(balloon.latitude)
                        && parseFloat(bound[1]) === parseFloat(balloon.longitude);
                });
            });

            currentBalloonData.value = balloons;
            context.emit('balloon-open');
        }

        function onMapInit(mapObject) {
            if (!mapObject) {
                return;
            }

            map.value = mapObject;
            context.emit('update:zoom', props.zoom);
        }

        function onBalloonOpenHandler({payload}) {
            currentBalloonData.value = [payload];
            currentCoords.value = [payload.latitude, payload.longitude];
            context.emit('balloon-open');
        }

        function onSwitchBalloonHandler(switchTo) {
            const negativeCondition = switchTo < 0 && currentBalloonIndex.value > 0;
            const positiveCondition = switchTo > 0 && currentBalloonIndex.value < currentBalloonData.value?.length - 1;

            if (negativeCondition || positiveCondition) {
                currentBalloonIndex.value += switchTo;
            }
        }

        return {
            map,
            currentCoords,
            currentBalloonData,
            currentBalloonIndex,
            mapOptions,
            markers,
            onClusterClickHandler,
            onMapInit,
            onBalloonOpenHandler,
            onSwitchBalloonHandler,
        };
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/components/about/offices/offices-map';
</style>
